import * as React from 'react';
import PQueue from 'p-queue'
import memoize from 'memoizee'
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import * as Moment from 'moment'
import { v4 as uuidv4 } from 'uuid';
import Enumerable from 'linq'
import { alpha, lighten, darken } from '@mui/material/styles';
import accentUtils from './helpers/AccentUtils'
import formHelper from './helpers/FormHelper'
import i18n from 'i18next';
import './helpers/app.myinsyte.strings.js'
import { getUser } from './UserService'
import {  extend} from 'string-format'
import { PhoneNumberUtil, PhoneNumberType } from 'google-libphonenumber'
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { OutsideAlerter } from '../controls/OutsiderAlerter';
import { navigateBackMainWindow } from '../app-framework/AccentRouter';
import { toLengthAsText } from '../controls/products/ProductConfiguratorShared';
const { findIana } = require('windows-iana');

extend(String.prototype);

const localToCurrencyCode = {
    "en-AU": "AUD",
    "en-NZ": "NZD",
    "en-GB": "GBP", // UK
    "en-CA": "CAD", // Canada
    "en-ZA": "ZAR", // South Africa
    "en-US": "USD", // US
    "en-IE": "EUR",  // Ireland
    "en-IN": "INR",  // India
    "en-SG": "SGD"  // Singapore
};

export const volumeUnits = {
    cubicMillimeters: 1000,         // 1 mm³ =    0.000000001 m³ →    0.000000001 * 1,000,000,000,000 =             1,000
    cubicCentimeters: 1000000,      // 1 cm³ =       0.000001 m³ →       0.000001 * 1,000,000,000,000 =         1,000,000
    cubicMeters: 1000000000000,     // 1 m³  =            1.0 m³ →            1.0 * 1,000,000,000,000 = 1,000,000,000,000
    cubicInches: 16387064,          // 1 in³ ≈ 0.000016387064 m³ → 0.000016387064 * 1,000,000,000,000 =        16,387,064
    cubicFeet: 28316846592,         // 1 ft³ ≈ 0.028316846592 m³ → 0.028316846592 * 1,000,000,000,000 =    28,316,846,592
    cubicYards: 764554857984        // 1 yd³ ≈ 0.764554857984 m³ → 0.764554857984 * 1,000,000,000,000 =   764,554,857,984
}


export const areaUnits = {
    squareMillimeters : 100,        // 1 mm² =   0.000001 m² →   0.000001 * 100,000,000 =         100
    squareCentimeters : 10000,      // 1 cm² =     0.0001 m² →     0.0001 * 100,000,000 =      10,000
    squareMeters : 100000000,       // 1 m²  =        1.0 m² →        1.0 * 100,000,000 = 100,000,000
    squareInches : 64516,           // 1 in² ≈ 0.00064516 m² → 0.00064516 * 100,000,000 =      64,516
    squareFeet : 9290304,           // 1 ft² ≈ 0.09290304 m² → 0.09290304 * 100,000,000 =   9,290,304
    squareYards : 83612736          // 1 yd² ≈ 0.83612736 m² → 0.83612736 * 100,000,000 =  83,612,736
}

export const measurementUnits = {
    millimeter: 10,                 // 1 mm   =       1.0 mm →       1.0 * 10 =         10
    centimeter: 100,                // 1 cm   =      10.0 mm →      10.0 * 10 =        100
    meter: 10000,                   // 1 m    =      25.4 mm →      25.4 * 10 =        254
    inch: 254,                      // 1 in   =    1000.0 mm →    1000.0 * 10 =     10,000
    foot: 3048,                     // 1 ft   =     304.8 mm →     304.8 * 10 =      3,048
    yard: 9144,                     // 1 yd   =     914.4 mm →     914.4 * 10 =      9,144
    kilometers: 10000000,           // 1 km   = 1,000,000 mm → 1,000,000 * 10 = 10,000,000
    miles: 16093440                 // 1 mile = 1,609,344 mm → 1,609,344 * 10 = 16,093,440
};

export const linealUnitFormats = {
    defaultNoUnits: 0,
    fullUnitName: 1,
    abbreviation: 2,
    inchesFractions: 4,
    feetInchesFractions: 8,
    yardsFeetInchesFractions: 16,
    kilometers: 32,
    miles: 64
};

export const precisionTypes = [
    { ID: 1, Description: "1", isForMetric: true, isForImperial: true, minMetricUnitType: measurementUnits.millimeter, minImperialUnitType: measurementUnits.inch },
    { ID: 0.5, Description: "1/2", isForMetric: true, isForImperial: true, minMetricUnitType: measurementUnits.centimeter, minImperialUnitType: measurementUnits.inch },
    { ID: 0.25, Description: "1/4", isForMetric: true, isForImperial: true, minMetricUnitType: measurementUnits.centimeter, minImperialUnitType: measurementUnits.inch },
    { ID: 0.125, Description: "1/8", isForMetric: false, isForImperial: true, minImperialUnitType: measurementUnits.inch },
    { ID: 0.1, Description: "1/10", isForMetric: true, isForImperial: false, minMetricUnitType: measurementUnits.centimeter },
    { ID: 0.0625, Description: "1/16", isForMetric: false, isForImperial: true, minImperialUnitType: measurementUnits.inch },
    { ID: 0.03125, Description: "1/32", isForMetric: false, isForImperial: true, minImperialUnitType: measurementUnits.inch },
    { ID: 0.015625, Description: "1/64", isForMetric: false, isForImperial: true, minImperialUnitType: measurementUnits.inch },
    { ID: 0.01, Description: "1/100", isForMetric: true, isForImperial: false, minMetricUnitType: measurementUnits.meter },
];


export const linealUnitFormatsTypes = [
    { ID: 0, Description: "Default (No Units)", isForMetric: true, isForImperial: true},
    { ID: 1, Description: "Full Unit Name", isForMetric: true, isForImperial: true },
    { ID: 2, Description: "Abbreviation", isForMetric: true, isForImperial: true },
    { ID: 4, Description: "Inches Fractions", isForMetric: false, isForImperial: true },
    { ID: 8, Description: "Feet Inches Fractions", isForMetric: false, isForImperial: true },
    { ID: 16, Description: "Yards Feet Inches Fractions", isForMetric: false, isForImperial: true }
];


let accentUserLang = "en-AU";

window.uuidv1 = uuidv4;

let currencySymbol = null;

const queue = new PQueue({ concurrency: 1 });

export function getCurrencyCode() {
    // Multiple Currencies not yet supported by RTL
    return localToCurrencyCode[accentUserLang];
}


export function windowsToIana(tz) {
    return findIana(tz);
}

export function proRata(total, rounding, items) {


    var baseItems = items.map(i => window.InsyteProduct.InsyteWeb.ProRataItem.FromJS(i.ID, i.BaseAmount));

    var calc = new window.InsyteProduct.InsyteWeb.ProrataCalculator();

    var result = calc.Calculate(new window.System.Decimal(total), rounding, baseItems);

    var resultAsArray = result.toArray ? result.toArray() : result;

    return resultAsArray.map(r => r.ToJS());


}

export function isDateBetween(targetDate, startDate, endDate) {
    if (!targetDate || !startDate || !endDate) {
        return false;
    }

    let target = new Moment(new Date(targetDate));
    let start = new Moment(new Date(startDate));
    let end = new Moment(new Date(endDate));

    return target.isBetween(start, end, null, '[]');
}


export function ConvertDateToBrowserTimezoneFromUTC(date) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return utcToZonedTime(new Date(date), browserTimezone);
}

export function ConvertDateFromBrowserTimezoneToUTC(date) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zonedTimeToUtc(new Date(date), browserTimezone);
}
export function ConvertDateToUTC(date, timeZone) {
    return zonedTimeToUtc(new Date(date), timeZone);
}
export function ConvertDateFromUTC(date, timeZone) {
    return utcToZonedTime(new Date(date), timeZone);
}

export function Today() {
    const local = new Date();
    local.setHours(0, 0, 0, 0);
    return local; // correct UTC instant for local start-of-day
}
export function TodayInTimeZone(timeZone) {
    if (!timeZone) {
        return Today();
    }

    const nowInTz = ConvertDateFromUTC(new Date(), timeZone);
    const dayInTz = new Date(nowInTz);
    dayInTz.setHours(0, 0, 0, 0);

    return ConvertDateToUTC(dayInTz, timeZone);
}

export async function waitForCondition(checkCondition, sleepMilliseconds = 200, maxSleepCount = 10) {

    let sleepCount = 0;

    function sleep() {
        return new Promise(function (p) {
            window.setTimeout(function () {
                sleepCount++;
                p();
            }, sleepMilliseconds);
        });
    }

    async function wait() {

        if (sleepCount >= maxSleepCount) {
            console.log("waitForCondition Max Sleep Count Exceeded");
            return;
        }

        if (!accentUtils.isNull(checkCondition) && !checkCondition()) {


            console.log("waitForCondition Sleeping");
            await sleep();
            await wait();

        } else {
            console.log("waitForCondition No Sleep");
        }
    }

    
    await wait();

}

export function compareObjects(obj1, obj2) {

    if (accentUtils.isNull(obj1) && !accentUtils.isNull(obj2)) return false;
    if (!accentUtils.isNull(obj1) && accentUtils.isNull(obj2)) return false;

    if (from(Object.keys(obj1)).any(k => obj1[k] !== obj2[k])) return false;
    if (from(Object.keys(obj2)).any(k => obj1[k] !== obj2[k])) return false;

    return true;
}

export function isMobileNumber(number, countryCode) {
    try {
        var numUtils = PhoneNumberUtil.getInstance();
        return numUtils.getNumberType(numUtils.parse(number, countryCode)) === PhoneNumberType.MOBILE;
    } catch (e) {
        return false;
    }
}

export function isPhoneNumber(number, countryCode) {

    try {
        var numUtils = PhoneNumberUtil.getInstance();
        return numUtils.isPossibleNumber(numUtils.parse(number, countryCode));
    } catch (e) {
        return false;
    }
}


export const enqueue = (task) => {
    
    queue.add(() => {
        console.log("Exec Queue..................................");
        return task();
    })
}

export function toastInfo(msg) {
    window.addToast(false, msg);
}
export function toastError(msg) {
    window.addToast(true, msg);
}

export const format = {

    formatDate: function (date, timezone) {

        if (timezone) {
            timezone = findIana(timezone)[0];
        }


        if (accentUtils.isEmpty(date)) {
            return '';
        }

        let dateObj;
        if (typeof date === 'string') {
            dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) {
                return '';
            }
        } else {
            dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) {
                return '';
            }
        }

        // Create options object only if timezone is provided
        const options = timezone ? { timeZone: timezone } : undefined;

        // This will behave like the original when timezone is not provided
        return Intl.DateTimeFormat(accentUserLang, options).format(dateObj);
    },

    formatDateTimeFileName: function (date) {


        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = Date.parse(date);
        }

        if (isNaN(date)) {
            return '';
        }

        date = new Date(date);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}-${minutes}-${seconds}`;
    },
    formatDateYearMonthDay: function (date) {
        

        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = Date.parse(date);
        }

        if (isNaN(date)) {
            return '';
        }

        date = new Date(date);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    },
    formatDuration: function (minutes) {
        const duration = Moment.duration(minutes, 'minutes');
        let formatted = [];
        if (duration.days() > 0) {
            formatted.push(duration.days() + ' d' + (duration.days() > 1 ? 's' : ''));
        }
        if (duration.hours() > 0) {
            formatted.push(duration.hours() + ' hr' + (duration.hours() > 1 ? 's' : ''));
        }
        if (duration.minutes() > 0) {
            formatted.push(duration.minutes() + ' min' + (duration.minutes() > 1 ? 's' : ''));
        }
        return formatted.join(', ');
    },
    formatTime: function (date, amPM = true, showSeconds = false, timezone) {
        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = new Date(date);
        }

        if (isNaN(date)) {
            return '';
        }

        if (timezone) {
            timezone = findIana(timezone)[0];
        }

        const options = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: amPM
        };

        if (showSeconds) {
            options.second = '2-digit';
        }

        if (timezone) {
            options.timeZone = timezone;
        }

        return new Intl.DateTimeFormat(accentUserLang, options).format(date);
    },


    formatDateTime: function (date, amPM = true, shortDate = true) {

        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = Date.parse(date);
        }

        if (isNaN(date)) {
            return '';
        }

        const options = {
            weekday: shortDate ? undefined : 'short', // "short", "long", or "narrow"
            year: 'numeric',
            month: shortDate ? 'numeric' : 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: amPM
        };


        return Intl.DateTimeFormat(accentUserLang, options).format(date);
    },
    formatDateMonthYear : function (date) {

        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = Date.parse(date);
        }

        if (isNaN(date)) {
            return '';
        }


        return Intl.DateTimeFormat(accentUserLang, { year: 'numeric', month: 'short' }).format(date);
    },

    formatDateDayMonth: function (date) {

        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = Date.parse(date);
        }

        if (isNaN(date)) {
            return '';
        }


        return Intl.DateTimeFormat(accentUserLang, { day: 'numeric', month: 'short' }).format(date);
    },

    formatDateDayOfWeekMonth: function (date) {

        if (accentUtils.isEmpty(date)) {
            return '';
        }

        if (typeof date === 'string') {
            date = Date.parse(date);
        }

        if (isNaN(date)) {
            return '';
        }


        return Intl.DateTimeFormat(accentUserLang, { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
    },


    toUTC : function (date) {

        var dte = new Date(date);

        return new Date(dte.getTime() + (dte.getTimezoneOffset() * 60000));
    },

    formatCurrency: function (value, emptyIfZero) {

      
        if (accentUtils.isNull(value)) {
            return '';
        }

        const roundedValue = Math.round(value * 100) / 100;
        if (Math.abs(roundedValue) < 0.01) {
            value = 0;
        }


        if (emptyIfZero && value === 0) {
            return '';
        }

        const currencyCode = localToCurrencyCode[accentUserLang] ?? localToCurrencyCode['en-AU'];

        return Intl.NumberFormat(accentUserLang, { style: 'currency', currency: currencyCode }).format(value);

    },

    isImerialLocal() {
        return accentUserLang === "en-US";
    },
    formatLength: function (length, units, format) {

        return toLengthAsText(length, units, format);

    },

    getCurrencySymbol: function () {
        if (currencySymbol === null) {
            currencySymbol = new Intl.NumberFormat(accentUserLang, { style: 'currency', currency: localToCurrencyCode[accentUserLang] }).formatToParts().find(part => part.type === 'currency').value;
        }

        return currencySymbol;
     },



    formatNumber : function (value, emptyIfZero, minDescimals) {

        if (!isFinite(value) || isNaN(value)) {
            if (emptyIfZero) {
                return '';
            } else {
                return 0;
            }
        }


        if (accentUtils.isNull(value)) {
            return '';
        }

        if (emptyIfZero && value === 0) {
            return '';
        }

        minDescimals = 2 || minDescimals;


        return Intl.NumberFormat(accentUserLang, { currency: localToCurrencyCode[accentUserLang], minimumFractionDigits: minDescimals }).format(accentUtils.round(value));
    },

    formatInt: function (value, emptyIfZero) {

        if (!isFinite(value) || isNaN(value)) {
            if (emptyIfZero) {
                return '';
            } else {
                return 0;
            }
        }


        if (accentUtils.isNull(value)) {
            return '';
        }

        if (emptyIfZero && value === 0) {
            return '';
        }

        const minDescimals = 0;


        return Intl.NumberFormat(accentUserLang, { currency: localToCurrencyCode[accentUserLang], minimumFractionDigits: minDescimals }).format(accentUtils.round(value));
    },



    formatPercentage : function (value, emptyIfZero, maximumSignificantDigits) {

        if (!isFinite(value) || isNaN(value)) {
            if (emptyIfZero) {
                return '';
            } else {
                return 0;
            }
        }


        if (accentUtils.isNull(value)) {
            return '';
        }

        if (emptyIfZero && value === 0) {
            return '';
        }


        maximumSignificantDigits = maximumSignificantDigits ?? 4;


        return Intl.NumberFormat(accentUserLang, { style: 'percent', currency: localToCurrencyCode[accentUserLang], maximumSignificantDigits: maximumSignificantDigits }).format(value);
    },

    formatTaxRatePercent: function (value, emptyIfZero) {
        if (!isFinite(value) || isNaN(value)) {
            return emptyIfZero ? '' : '0%';
        }

        if (accentUtils.isNull(value)) {
            return '';
        }

        if (emptyIfZero && value === 0) {
            return '';
        }

        return `${Intl.NumberFormat(accentUserLang, { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(value)}%`;
    }


};


const internalFormat = format;



export class A extends React.PureComponent {

    constructor(props) {
        super(props);

        this.linkID = `link_${newGuid() }`;

        this.offset = {
            left: 0,
            top: 0,
        };

        this.state = {
            showContext: false
        };

        this.onClick = this.onClick.bind(this);
        this.handleContextMenu = this.handleContextMenu.bind(this);
        this.onAlert = this.onAlert.bind(this);
        this.onContextMenuSelect = this.onContextMenuSelect.bind(this);
        this.hasLinkID = this.hasLinkID.bind(this);
    }

    hasLinkID(linkID, target) {
        let element = target;
        while (element !== document.body) {
            if (element.hasAttribute('data-linkid') && element.getAttribute('data-linkid') === linkID) {
                return true;
            }
            element = element.parentNode;
        }
     
        return false;
    }


    onContextMenuSelect(e) {
        e.nativeEvent.preventDefault();
        this.setState({ showContext: false });

        window.open(this.props.href, '_blank')
    }

    onAlert(e) {
        if (e.target.hasAttribute('data-linkid') && e.target.getAttribute('data-linkid') === this.linkID) return;

        this.setState({ showContext: false })

    }

    handleContextMenu(e) {
        this.offset = {
            left: e.pageX,
            top: e.pageY,
        };
        e.preventDefault();
        this.setState({showContext : true});
    }

    onClick(e) {

        if (!this.hasLinkID(this.linkID,  e.target)) return;
        
        goTo(this.props.href).then(allow => {

            if (!allow) return;

            if (this.props.onClick) {
                this.props.onClick(e);
            }

        });

    }

    render() {
        return <div
            {...this.props}
            data-linkid={ this.linkID}
            className={this.props.className ?? 'acc-link'}
            style={{ cursor: "pointer", ...this.props.style }}
            onClick={this.onClick}
            onContextMenu={this.handleContextMenu}
        >
            {this.props.children}
            
            <Popup show={this.state.showContext} offset={this.offset}>
                < OutsideAlerter onAlert={this.onAlert}>
                    <Menu
                        onSelect={ this.onContextMenuSelect }
                        vertical={true}
                        style={{
                            display: "inline-block",
                        }}
                    >
                        <MenuItem text="Open in new tab" />
                    </Menu>
                </OutsideAlerter>
            </Popup>
        </div>;
    }

}


export const t = (str, paramsObj) => {

    if (!paramsObj) {
        paramsObj = {};
    }

    paramsObj.accentCurrencySymbol = format.getCurrencySymbol();
    
    return i18n.t(str, paramsObj);

}
 
export function from(array) {
    return Enumerable.from(array);
}

/** Sort order for fields: other=0, Min*=1, Max*=2 (ensures Min immediately before Max, others first) */
export const minBeforeMaxSortOrder = (name) => {
    const n = name || "";
    if (n.startsWith("Min")) return 1;
    if (n.startsWith("Max")) return 2;
    return 0;
};

export function def(value, defaultValue) {

    if (accentUtils.isNull(value)) {
        return defaultValue;
    }

    return value;

};

export function newGuid() {    
    return uuidv4();
};



export function showOK(title, body, buttons, asTextArea, className, noCancel, maxWidth) {
        return window.insytwebOKDlg.open(title, body, buttons, asTextArea, className, noCancel, maxWidth);
};



export function showDialog(dialog, onShown) {

    let closeDlg = null;

    return new Promise(p => {

        const isFunction = typeof dialog === "function";

        const props = {
            onClose: e => {
                closeDlg().then(() => {
                    p(e);
                });
                
            }
        };

        closeDlg = window.insytwebAppDlg.setDisplayItem(
            isFunction ? dialog(props) : React.cloneElement(dialog, props),
            onShown,
            e => {            
                p({canceled: true, forceCanceled: true});
            }
        );

    });

    
};

export function reloading() {
    const isReloading = localStorage.getItem("reloading");
    if (isReloading !== undefined) {
        if (isReloading !== null) {
            return isReloading === "true";
        }
    }
    return false;
}
export function clearReloading() {
    localStorage.setItem("accent_reloading", false);
}

export function getLoginParameters() {
    var deviceId = localStorage.getItem("accent_context");
    if (!deviceId) {
        deviceId = uuidv4();
        localStorage.setItem("accent_context", deviceId);
    }
    const urlParams = new URLSearchParams(window.location.search);
    const stateParam = urlParams.get('state');
    const impersonateEmail = urlParams.get('impersonateEmail');
   return { accentDeviceId: deviceId, redirectState: stateParam, impersonateEmail:impersonateEmail };
    
};

export function showWait(ref) {
    return window.insytwebAppDlg.showWait(ref);
};

export function processVersion(version) {

    if (!accentUtils.isEmpty(version) && false) {

        if (!accentUtils.isNull(window.insyteAppVersion) && window.insyteAppVersion !== version && window.insytwebOKDlg) {

            showOK("application_strings.application.dialogs.newVersionTitle", "application_strings.application.dialogs.newVersionMsg", ["application_strings.application.buttons.ok"], false, null, true).then(function (btn) {
                window.location.hash = "";
                window.location.reload();
            });

        }
    }
};

let oneTimeCache = {};

export function cacheAdd(item, key=null) {
    const itemID =  key??`c${newGuid()}`;

    oneTimeCache[itemID] = item;

    return itemID;
};

export function cacheGet(itemID) {

    if (accentUtils.isNull(itemID)) return null;

    const res = oneTimeCache[itemID];

    return res;
};

export function cacheRemove(itemID) {

    if (accentUtils.isNull(itemID)) return null;

    const res = oneTimeCache[itemID];

    delete oneTimeCache[itemID];

    return res;
};

export function cancelAllDialogs() {
    if (window.insytwebAppDlg) {
        return window.insytwebAppDlg.cancelAllDialogs();
    }
}


export const toolbarActionStates = {
    saving : "SAVING",
    closing : "CLOSING",
    undoing : "UNDOING"
};



let toolbarAction = null;

export function getToolbarActionState() {
    return toolbarAction;
}

export function setToolbarActionState(state) {
    toolbarAction = state;
}


let navigationKey = 0;

function incrementNavigationKey() {
    navigationKey = (navigationKey + 1) % 10000000000000;
}

export function getNavigationKey() {
    return navigationKey;
}

export function goBack() {

    return new Promise(p => {

        const canSave = window.accentAutoSave ? window.accentAutoSave({navigatingBack: true}) : Promise.resolve({ allow: true });


        canSave.then(saveResult => {

            if (saveResult.allow) {
                incrementNavigationKey();    

                cancelAllDialogs().then(() => {
                    navigateBackMainWindow();
                });                
            }

            p(saveResult.allow);
        });


    });

    
}

export function goTo(path, additionalViewData = null) {

    return new Promise(p => {

        const canSave = window.accentAutoSave ? window.accentAutoSave() : Promise.resolve({ allow: true });


        canSave.then(saveResult => {

            if (saveResult.allow) {

                cancelAllDialogs().then(() => {

                    if (!accentUtils.isNull(additionalViewData)) {
                        cacheAdd(additionalViewData, "AdditionalViewData");
                    }


                    const finalPath = path.startsWith("/") ? path : `/${path}`;

                    incrementNavigationKey();

                    if (saveResult.replace || finalPath === window.accentRouter.location.pathname) {
                        window.accentRouter.replace(finalPath);
                    } else {
                        window.accentRouter.push(finalPath);
                    }


                });

                
            }

            p(saveResult.allow);
        });


    });

    
    
};

export async function goToCreated(path, entity, additionalViewData = null) {
    const cacheID = cacheAdd({ entity, created: true });

    // Ensure the path starts with "/"
    if (!path.startsWith("/")) {
        path = `/${path}`;
    }

    // Split the path from any existing query string
    const [basePath, queryString] = path.split('?');

    // Append /NEW to the basePath
    const newPath = `${basePath}/NEW`;

    // Construct the final query string
    // Check if the original path included a query string, and append it with the new parameter
    const finalQueryString = queryString ? `${queryString}&newEntity=${cacheID}` : `newEntity=${cacheID}`;

    // Combine newPath with the finalQueryString
    const finalPath = `${newPath}?${finalQueryString}`;

    goTo(finalPath, additionalViewData);
}





export async function goToNew(path, recordType, defaults, parent = null, custom = null, additionalViewData = null, onCreate = null) {

    

    const cacheID = cacheAdd({ recordType, defaults, parent, custom, onCreate });

    if (path.startsWith("/"))
        goTo(`${path}/NEW?newEntity=${cacheID}`, additionalViewData);
    else
        goTo(`/${path}/NEW?newEntity=${cacheID}`, additionalViewData);




};



export const useMousePos = () => {

    const [position, setPosition] = React.useState({ x: 0, y: 0 });

    React.useEffect(() => {
        const setFromEvent = (e) => setPosition({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", setFromEvent);

        return () => {
            window.removeEventListener("mousemove", setFromEvent);
        };
    }, []);

    return position;



};



export function getColorAlpha(color, opacity){

    if (accentUtils.isEmpty(color)) return color;

    return alpha(color, opacity);

};

export function getColorLighter(color, perc){

    if (accentUtils.isEmpty(color)) return color;

    return lighten(color, perc);

};

export function getColorDarker(color, perc) {
    if (accentUtils.isEmpty(color)) return color;

    return darken(color, perc);
};


export const useMouseUp = () => {

    const [up, setUp] = React.useState(false);

    React.useEffect(() => {
        const setUpEvent = (e) => setUp(true);
        const setDownEvent = (e) => setUp(false);
        window.addEventListener("mouseup", setUpEvent);
        window.addEventListener("mousedown", setDownEvent);

        return () => {
            window.removeEventListener("mouseup", setUpEvent);
            window.removeEventListener("mousedown", setDownEvent);
        };
    }, []);

    return up;



};


class ViewStateHelper {

    constructor() {
        this.state = {};
        this.stateDates = {};

        this.getState = this.getState.bind(this);
        this.setState = this.setState.bind(this);
        this.clearState = this.clearState.bind(this);
    }

    getState(id, defaultVal) {
        if (accentUtils.isNull(this.state[id])) {
            this.state[id] = defaultVal;
        }

        return this.state[id];
    }

    setState(id, value) {
        this.state[id] = value;

    }

    clearState(id) {
        this.state[id] = null;

    }
}



export async function InitializeHelpers() {

    const user = getUser();

    if (!accentUtils.isNull(user)) {
        accentUserLang = user.Locale;
    }

    await i18n.init({
        lng: 'en',
        debug: false,
        resources: window.myinsyteresources,
        fallbackLng: false,
        interpolation: {
            escapeValue: false,
            format: (value, formatStr, lng) => {

                if (formatStr) {

                    if (formatStr === "dateTime") {
                        return internalFormat.formatDateTime(value);
                    } else {
                        return internalFormat.formatDate(value);
                    }

                }
                return value;
            }
        }
    }, function (err, t) {

    });


}


const viewStateHelper = new ViewStateHelper();

export { accentUtils, formHelper, accentUserLang, memoize, viewStateHelper };


window.memoize = memoize;
